import { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  CircularProgress,
  Divider,
} from "@mui/material";
import {
  Delete,
  Close,
  Person,
  Email,
  Schedule,
  OpenInNew,
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import useAdminAPIClient from "../../api/admin/hooks/useAdminAPIClient";
import {
  setAdminConversations,
  setAdminUsers,
} from "../../redux/actions/admin";

const formatDate = (timestamp) => {
  return new Date(timestamp).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const DeletionRequestCard = ({
  request,
  onProcess,
  processing,
  onCancel,
  cancelling,
  onViewTasks,
  userData,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  const handleConfirmDelete = () => {
    setConfirmOpen(false);
    onProcess(request);
  };

  const handleConfirmCancel = () => {
    setCancelConfirmOpen(false);
    onCancel(request);
  };

  const tasksCreated = userData?.tasksCreated || 0;
  const tasksCompleted = userData?.tasksCompleted || 0;
  const completionRate =
    tasksCreated > 0 ? Math.round((tasksCompleted / tasksCreated) * 100) : 0;
  const totalVisits = userData?.totalVisits || 0;

  return (
    <>
      <Card
        sx={{
          mb: 2,
          border: "2px solid #d32f2f",
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 2,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Person fontSize="small" />
                {request.displayName || "Unknown User"}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Email fontSize="small" />
                {request.email}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}
              >
                <Schedule fontSize="small" />
                Requested: {formatDate(request.sentAt)}
              </Typography>
            </Box>
            <Chip label="Pending" color="error" size="small" />
          </Box>

          <Typography variant="body2" sx={{ mb: 2, fontFamily: "monospace" }}>
            User ID: {request.userId}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ mb: 3 }}>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", display: "block", mb: 1.5 }}
            >
              USER ACTIVITY
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 2,
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {tasksCreated}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Tasks Created
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {tasksCompleted}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Tasks Completed
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color:
                      completionRate > 75
                        ? "#4caf50"
                        : completionRate > 50
                          ? "#ff9800"
                          : "#f44336",
                  }}
                >
                  {completionRate}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Completion Rate
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {totalVisits}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Visits
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              color="error"
              startIcon={
                processing ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Delete />
                )
              }
              onClick={() => setConfirmOpen(true)}
              disabled={processing || cancelling}
              sx={{ flex: 1, minWidth: 150 }}
            >
              {processing ? "Deleting..." : "Delete User & All Data"}
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={
                cancelling ? <CircularProgress size={16} /> : <Close />
              }
              onClick={() => setCancelConfirmOpen(true)}
              disabled={processing || cancelling}
              sx={{ flex: 1, minWidth: 120 }}
            >
              {cancelling ? "Cancelling..." : "Cancel"}
            </Button>
            <Button
              variant="outlined"
              endIcon={<OpenInNew />}
              onClick={() => onViewTasks(request.userId)}
              disabled={processing || cancelling}
              sx={{ flex: 1, minWidth: 120 }}
            >
              View Tasks
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle sx={{ color: "#d32f2f" }}>
          Confirm Account Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently delete this user and all their
            data?
          </DialogContentText>
          <Box sx={{ mt: 2, p: 2, bgcolor: "#fff3e0", borderRadius: 1 }}>
            <Typography variant="body2">
              <strong>User:</strong> {request.displayName}
            </Typography>
            <Typography variant="body2">
              <strong>Email:</strong> {request.email}
            </Typography>
            <Typography variant="body2">
              <strong>User ID:</strong> {request.userId}
            </Typography>
          </Box>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone. All user data will be permanently
            removed.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Yes, Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancelConfirmOpen}
        onClose={() => setCancelConfirmOpen(false)}
      >
        <DialogTitle>Cancel Deletion Request</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel this deletion request? The user will
            not be deleted and this request will be removed from the pending
            list.
          </DialogContentText>
          <Box sx={{ mt: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
            <Typography variant="body2">
              <strong>User:</strong> {request.displayName}
            </Typography>
            <Typography variant="body2">
              <strong>Email:</strong> {request.email}
            </Typography>
            <Typography variant="body2">
              <strong>User ID:</strong> {request.userId}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelConfirmOpen(false)}>
            Keep Request
          </Button>
          <Button
            onClick={handleConfirmCancel}
            color="primary"
            variant="contained"
          >
            Yes, Cancel Request
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const DeletionRequests = ({ onSwitchTab, onSetTaskSearchTerm }) => {
  const dispatch = useDispatch();
  const { deleteUser, getConversations, getUsers, markMessagesAsRead } =
    useAdminAPIClient();
  const { conversations = {}, users = [] } = useSelector(
    (state) => state.admin,
  );
  const [processingId, setProcessingId] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Create a map of userId to user for quick lookup
  const userMap = useMemo(() => {
    const map = {};
    users.forEach((user) => {
      map[user.userId] = user;
    });
    return map;
  }, [users]);

  // Extract deletion requests from conversations
  // Deletion requests are messages with messageType === "deletion-request"
  // Filter out messages that have been marked as read (processed or cancelled)
  const deletionRequests = useMemo(() => {
    const requests = [];
    Object.entries(conversations).forEach(([conversationId, messages]) => {
      messages.forEach((msg) => {
        if (msg.messageType === "deletion-request" && !msg.read) {
          requests.push({
            ...msg,
            conversationId,
          });
        }
      });
    });
    // Sort by date, newest first
    return requests.sort((a, b) => b.sentAt - a.sentAt);
  }, [conversations]);

  const pendingCount = deletionRequests.length;

  const handleProcessRequest = async (request) => {
    setProcessingId(request.messageId);
    setError(null);
    setSuccess(null);

    try {
      // Delete the user
      await deleteUser(request.userId);

      // Mark the deletion request message as read
      await markMessagesAsRead([request.messageId]);

      setSuccess(
        `Successfully deleted user ${request.displayName} (${request.email})`,
      );

      // Refresh conversations and users
      const [conversationsData, usersData] = await Promise.all([
        getConversations(),
        getUsers(),
      ]);
      if (conversationsData?.conversations) {
        dispatch(setAdminConversations(conversationsData.conversations));
      }
      if (usersData?.users) {
        dispatch(setAdminUsers(usersData.users));
      }
    } catch (err) {
      console.error("Failed to process deletion request:", err);
      setError(`Failed to delete user: ${err.message || "Unknown error"}`);
    } finally {
      setProcessingId(null);
    }
  };

  const handleCancelRequest = async (request) => {
    setProcessingId(request.messageId);
    setError(null);
    setSuccess(null);

    try {
      // Mark the deletion request message as read (which effectively cancels it)
      await markMessagesAsRead([request.messageId]);

      setSuccess(
        `Deletion request cancelled for ${request.displayName} (${request.email})`,
      );

      // Refresh conversations
      const data = await getConversations();
      if (data?.conversations) {
        dispatch(setAdminConversations(data.conversations));
      }
    } catch (err) {
      console.error("Failed to cancel deletion request:", err);
      setError(
        `Failed to cancel deletion request: ${err.message || "Unknown error"}`,
      );
    } finally {
      setProcessingId(null);
    }
  };

  const handleViewTasks = (userId) => {
    if (onSwitchTab) {
      onSwitchTab("4"); // Switch to Tasks tab
    }
    if (onSetTaskSearchTerm) {
      onSetTaskSearchTerm(userId); // Filter tasks by this user
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5">Account Deletion Requests</Typography>
        {pendingCount > 0 && (
          <Chip label={`${pendingCount} pending`} color="error" size="small" />
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {deletionRequests.length === 0 ? (
        <Box
          sx={{
            p: 4,
            textAlign: "center",
            bgcolor: "#f5f5f5",
            borderRadius: 2,
          }}
        >
          <Typography color="text.secondary">No deletion requests</Typography>
        </Box>
      ) : (
        deletionRequests.map((request) => (
          <DeletionRequestCard
            key={request.messageId}
            request={request}
            onProcess={handleProcessRequest}
            processing={processingId === request.messageId}
            onCancel={handleCancelRequest}
            cancelling={processingId === request.messageId}
            onViewTasks={handleViewTasks}
            userData={userMap[request.userId]}
          />
        ))
      )}
    </Box>
  );
};

export default DeletionRequests;
